'use client';
import { ReactLenis } from 'lenis/react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useLayoutEffect, useMemo } from 'react';

import { AdaptiveLogo } from '@/components/adaptive-logo';
import { AnimatedFooter } from '@/components/animated-footer';
import { Background } from '@/components/background';
import { ScrollToTop } from '@/components/scroll-top';
import { spotlights } from '@/data/site-content';
import { useAspectRatio } from '@/helpers/hooks';
import { Footer } from '@/modules/footer';
import { NeedHelp } from '@/modules/need-help';
import { Partners } from '@/modules/partners';


const LandscapeServicesSection = dynamic(
  () => import('@/components/services-section').then((mod) => ({ default: mod.LandscapeServicesSection })),
  {
    ssr: false,
    loading: () => <div className="h-[640dvh] w-full" />,
  },
);

const MobileServicesSection = dynamic(
  () => import('@/components/services-section/mobile-services').then((mod) => ({ default: mod.MobileServicesSection })),
  {
    ssr: false,
    loading: () => <div className="h-[640dvh] w-full" />,
  },
);

const Highlight = dynamic(() => import('@/modules/highlight').then((mod) => ({ default: mod.Highlight })), {
  ssr: false,
});

export const ClientPage = () => {
  const { isLandscape } = useAspectRatio();

  const horizontalSpeed = isLandscape ? 0.75 : 1;

  const lenisConfig = useMemo(
    () =>
      isLandscape
        ? { lerp: 0.01, duration: 2, smoothWheel: true }
        : { syncTouch: true, touchMultiplier: 0.7, touchInertiaExponent: 2, syncTouchLerp: 0.07 },
    [isLandscape],
  );

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const setScrollbarWidth = () => {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    };

    setScrollbarWidth();
    window.addEventListener('resize', setScrollbarWidth);

    return () => {
      window.removeEventListener('resize', setScrollbarWidth);
    };
  }, []);

  return (
    <ReactLenis key={isLandscape ? 'landscape' : 'portrait'} root options={lenisConfig}>
      <Background />

      <Link href="/">
        <AdaptiveLogo blendMode={false} aria-label="Go to All Turtles Home" />
      </Link>

      <div className="relative" style={{ zIndex: 60 }}>
        {isLandscape ? <LandscapeServicesSection /> : <MobileServicesSection />}
      </div>

      <div id="highlight-section" className="relative z-0" style={{ transition: 'background 350ms ease' }}>
        {spotlights?.bentoville && (
          <Highlight {...spotlights?.bentoville} sectionId="bentoville1" horizontalScrollSpeed={horizontalSpeed} />
        )}
        {spotlights?.airtime && (
          <Highlight {...spotlights?.airtime} sectionId="mmhmm" horizontalScrollSpeed={horizontalSpeed} />
        )}
        {spotlights?.carrot && (
          <Highlight {...spotlights?.carrot} sectionId="carrot" horizontalScrollSpeed={horizontalSpeed} />
        )}
        {spotlights?.sora && (
          <Highlight {...spotlights?.sora} sectionId="sora" horizontalScrollSpeed={horizontalSpeed} />
        )}
      </div>
      <div className="relative" style={{ zIndex: 60 }}>
        <Partners />
        <NeedHelp />
        <AnimatedFooter />
      </div>
    </ReactLenis>
  );
};
